import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHome, FaUser, FaGraduationCap, FaTicketAlt, FaCogs,
  FaProjectDiagram, FaChartPie, FaClipboardCheck, FaUsers,
  FaCalendar, FaBars, FaTimes, FaQrcode, FaPowerOff, FaBell
} from 'react-icons/fa';
import UserAvatar from './UserAvatar';

const API_ROLE = 'https://dev.sliitleo.org/Backend/user_role.php';
const API_ROLES = 'https://dev.sliitleo.org/Backend/roles.php';
const API_PATHS = 'https://dev.sliitleo.org/Backend/paths.php';
const API_ROLE_PATHS = 'https://dev.sliitleo.org/Backend/role_paths.php';

const menuItemsConfig = [
  { icon: <FaHome/>,             label: 'Home',             route: '/home',               exact: true },
  { icon: <FaUser/>,             label: 'My Profile',       route: '/profile',            exact: true },
  { icon: <FaGraduationCap/>,    label: 'Leo Education',    route: '/education',          exact: true },
  { icon: <FaTicketAlt/>,        label: 'My Tickets',       route: '/tickets',            exact: true },
  { icon: <FaCalendar/>,         label: 'Club Calendar',    route: '/club-calander',      exact: true },
  { separator: true },
  { icon: <FaCogs/>,             label: 'Admin Console',    route: '/admin',              exact: false },
  { icon: <FaProjectDiagram/>,   label: 'Club Projects',    route: '/club-projects',      exact: true },
  { icon: <FaChartPie/>,         label: 'Club Dashboard',   route: '/club-dashboard',     exact: true },
  { icon: <FaClipboardCheck/>,   label: 'Reporting Hub',    route: '/reporting-hub',      exact: true },
  { icon: <FaUsers/>,            label: 'Installation',     route: '/installation',       badge: null, exact: true },
  { icon: <FaGraduationCap/>,    label: 'Education Admin',  route: '/education-statistics', exact: true },
  { icon: <FaQrcode/>,           label: 'Attendance',       route: '/attendance',         exact: true },
];

// helpers
const readStoredUser = () => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } };
const normalizeRole = (role) =>
  String(role || '')
    .toLowerCase()
    .replace(/[.\-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// mid-tier roles list (unchanged)
const MID_ROLES = new Set([
  'secretary',
  'treasurer',
  'co asst treasurer',
  'co asst secretary',
  'co assistant treasurer',
  'installation sub committee member',
  'co assistant secretary',
]);

const MID_ROLE_ALLOWED_LABELS = new Set([
  'Home',
  'My Profile',
  'Leo Education',
  'My Tickets',
  'Club Calendar',
  'Reporting Hub',
  'Club Projects',
  'Installation',
]);

// NEW: restricted roles list you gave — show member-only by default
const RESTRICTED_ROLES = new Set([
  'chief editor',
  'president program coordinator',
  'club performence evaluator',      // keep given spelling
  'club performance evaluator',      // and a safer variant just in case
  'club director',
  'team head',
  'activity director',
  'past president',
  'past board member',
  'it department',
  'installation sub committee member',  // normalized “sub-committee”
  'reporting sub committee',
  'committee member',
]);

// NEW: roles that also see Installation
const SEE_INSTALLATION_ROLES = new Set([
  'installation sub committee member',
  'president program coordinator',
  'club performence evaluator',
  'club performance evaluator',
  'secretary',
  'treasurer',
]);

// NEW: roles that also see Reporting Hub
const SEE_REPORTING_HUB_ROLES = new Set([
  'club performence evaluator',
  'club performance evaluator',
  'reporting sub committee',
]);

const baseMemberMenu = () => {
  const cut = menuItemsConfig.findIndex(i => i.separator);
  return cut === -1 ? [...menuItemsConfig] : menuItemsConfig.slice(0, cut);
};

const findItem = (label) => menuItemsConfig.find(i => i.label === label);

// True admins only (System Admin / President) -- mirrors ADMIN_ROLES in
// src/config/roles.js and Backend/birthday_roles.php's
// ADMIN_ROLES_FOR_BIRTHDAY_PUSH exactly, so "only admins see this" means
// the same set of people everywhere in the app, not just "leaders" (which
// the "everyone else" branch below is much broader than).
const ADMIN_NAV_ROLES = new Set(['system admin', 'president', 'club president']);
const NOTIFICATION_SETTINGS_ITEM = {
  icon: <FaBell/>, label: 'Notification Settings', route: '/notification-settings', exact: true,
};

// main builder
const buildMenuByRole = (role) => {
  const norm = normalizeRole(role);
  const isAdmin = ADMIN_NAV_ROLES.has(norm);

  // Member only
  if (norm === 'club member' || norm === 'member') {
    return baseMemberMenu();
  }

  // Your “restricted list”: member-only + specific extras
  if (RESTRICTED_ROLES.has(norm)) {
    const list = baseMemberMenu();

    if (SEE_INSTALLATION_ROLES.has(norm)) {
      const installation = findItem('Installation');
      if (installation) list.push(installation);
    }
    if (SEE_REPORTING_HUB_ROLES.has(norm)) {
      const reportHub = findItem('Reporting Hub');
      if (reportHub) list.push(reportHub);
    }
    if (isAdmin) list.push(NOTIFICATION_SETTINGS_ITEM);
    return list;
  }

  // Mid-tier (secretariat/finance)
  if (MID_ROLES.has(norm)) {
    const list = menuItemsConfig.filter(
      item => !item.separator && MID_ROLE_ALLOWED_LABELS.has(item.label)
    );
    if (isAdmin) list.push(NOTIFICATION_SETTINGS_ITEM);
    return list;
  }

  // Everyone else (leaders/admins) gets full menu, plus Notification
  // Settings only for the narrower true-admin set above.
  return isAdmin ? [...menuItemsConfig, NOTIFICATION_SETTINGS_ITEM] : menuItemsConfig;
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [menuItems, setMenuItems] = useState(buildMenuByRole('Club Member'));
  const [role, setRole] = useState('Club Member');
  const [profile, setProfile] = useState(() => readStoredUser());

  const [isOpen, setIsOpen] = useState(() => window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [expandedItems, setExpandedItems] = useState({});
  // Optional per-role override from the "Role & Path Management" admin page
  // (roles/paths/role_paths tables). null = no override configured for this
  // role -> fall back to the hardcoded buildMenuByRole() list untouched, so
  // roles nobody has explicitly configured there behave exactly as before.
  const [allowedRoutes, setAllowedRoutes] = useState(null);

  // fetch role
  useEffect(() => {
    const stored = readStoredUser();
    setProfile(stored);
    const payload =
      stored.SLIITLeoID ? { id: stored.SLIITLeoID } :
      (stored.email || stored.Email) ? { email: stored.email || stored.Email } :
      null;

    if (!payload) {
      const r = stored.role || 'Club Member';
      setRole(r);
      setMenuItems(buildMenuByRole(r));
      return;
    }

    (async () => {
      try {
        const res = await fetch(API_ROLE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        const r = (data?.success && (data.user?.role || 'Club Member')) || (stored.role || 'Club Member');
        setRole(r);
        setMenuItems(buildMenuByRole(r));
        localStorage.setItem('user', JSON.stringify({ ...stored, role: r }));
      } catch {
        const r = stored.role || 'Club Member';
        setRole(r);
        setMenuItems(buildMenuByRole(r));
      }
    })();
  }, []);

  // Optional nav override, configured per-role on the Role & Path Management
  // page. Only takes effect once an admin has explicitly saved at least one
  // path for this exact role name -- otherwise `allowedRoutes` stays null
  // and every existing role keeps its current hardcoded menu, unchanged.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rolesRes, pathsRes] = await Promise.all([fetch(API_ROLES), fetch(API_PATHS)]);
        const rolesBody = await rolesRes.json();
        const pathsBody = await pathsRes.json();
        if (!rolesBody?.success || !pathsBody?.success) throw new Error('bad response');

        const matchedRole = (rolesBody.data || []).find(
          (r) => r.name?.toLowerCase() === role.toLowerCase()
        );
        if (!matchedRole) {
          if (!cancelled) setAllowedRoutes(null);
          return;
        }

        const rpRes = await fetch(`${API_ROLE_PATHS}?roleId=${matchedRole.id}`);
        const rpBody = await rpRes.json();
        if (!rpBody?.success || !Array.isArray(rpBody.data) || rpBody.data.length === 0) {
          if (!cancelled) setAllowedRoutes(null);
          return;
        }

        const keyToRoute = new Map((pathsBody.data || []).map((p) => [p.key, p.route]));
        const routes = new Set(
          rpBody.data.map((entry) => keyToRoute.get(entry.key)).filter(Boolean)
        );
        if (!cancelled) setAllowedRoutes(routes.size > 0 ? routes : null);
      } catch {
        if (!cancelled) setAllowedRoutes(null);
      }
    })();
    return () => { cancelled = true; };
  }, [role]);

  const menuItemsWithState = useMemo(
    () =>
      menuItems
        // Notification Settings is gated purely by the ADMIN_NAV_ROLES check
        // in buildMenuByRole() above, not by the configurable role_paths
        // list (it isn't one of the 12 rows in the `paths` table), so it
        // must never be caught by this filter once it's already been added.
        .filter((item) => !allowedRoutes || item.separator || item.route === NOTIFICATION_SETTINGS_ITEM.route || allowedRoutes.has(item.route))
        .map((item) => ({
          ...item,
          isActive:
            item.route &&
            (item.exact ? location.pathname === item.route : location.pathname.startsWith(item.route)),
        })),
    [menuItems, location.pathname, allowedRoutes]
  );

  // responsive open/close
  useEffect(() => {
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const mobile = window.innerWidth < 1024;
        setIsMobile(mobile);
        setIsOpen(!mobile);
      }, 100);
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, []);

  // installation badge (if present in current menu)
  useEffect(() => {
    const run = async () => {
      try {
        const res = await fetch('https://dev.sliitleo.org/Backend/installation_total.php');
        if (!res.ok) throw new Error('Bad network');
        const data = await res.json();
        if (typeof data.total === 'number') {
          setMenuItems((items) =>
            items.map((it) => (it.label === 'Installation' ? { ...it, badge: data.total } : it))
          );
        }
      } catch (e) {
        console.error('Failed to fetch installation total:', e);
      }
    };
    run();
  }, []);

  const toggleMenu   = useCallback(() => setIsOpen((p) => !p), []);
  const toggleExpand = useCallback((label) => setExpandedItems((prev) => ({ ...prev, [label]: !prev[label] })), []);
  const handleLogout = useCallback(() => {
    try { localStorage.clear(); sessionStorage.clear(); } catch {}
    navigate('/login');
    if (isMobile) setIsOpen(false);
  }, [navigate, isMobile]);

  return (
    <>
      {isMobile && !isOpen && (
        <button
          onClick={toggleMenu}
          className="fixed top-4 left-4 z-40 p-2 rounded-lg text-white md:hidden"
          aria-label="Open menu"
          style={{
            background: 'var(--overlay-panel-bg)',
            border: '1px solid rgba(255,255,255,.10)',
            backdropFilter: 'blur(10px)',
            // iOS home-screen install: keeps this out from under the
            // status bar/notch. Resolves to plain 1rem everywhere else.
            top: 'calc(1rem + env(safe-area-inset-top))',
          }}
        >
          <FaBars size={18} />
        </button>
      )}

      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 bg-black z-30 md:hidden"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <motion.aside
        initial={{ x: isMobile ? -320 : 0 }}
        animate={{
          x: isOpen ? 0 : -320,
          opacity: 1,
          display: isMobile ? 'block' : 'flex',
        }}
        transition={{ type: 'spring', stiffness: 520, damping: 38 }}
        className="sidebar fixed top-0 left-0 h-screen w-64 z-40 flex flex-col p-3"
        aria-label="Main navigation"
      >
        {isMobile && (
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-3 right-3 p-2 rounded-lg text-white/90 hover:text-white"
            aria-label="Close menu"
            style={{
              background: 'var(--overlay-panel-bg)',
              border: '1px solid rgba(255,255,255,.10)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <FaTimes size={14} />
          </button>
        )}

        <div className="org" style={{ padding: '8px 8px 20px' }}>
          <img
            src="/club.png"
            alt="Leo Club"
            className="org-mark"
            style={{ objectFit: 'cover', background: 'none' }}
            onError={(e) => { e.currentTarget.src = '/default-logo.png'; }}
            draggable="false"
          />
          <div>
            <div className="org-name">Leo Club of SLIIT</div>
            <div className="org-sub">District 306 D6</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto" style={{ overflowX: 'hidden' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} className="nav-group">
            {menuItemsWithState.map((item, index) => {
              if (item.separator) {
                return (
                  <li key={`sep-${index}`} className="nav-label eyebrow" style={{ marginTop: 16, marginBottom: 6 }}>
                    Administration
                  </li>
                );
              }
              const isActive = item.isActive;
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = !!expandedItems[item.label];

              return (
                <li key={item.label} style={{ listStyle: 'none' }}>
                  <motion.div
                    whileTap={{ scale: 0.98 }}
                    className={`nav-item${isActive ? ' active' : ''}`}
                    onClick={() => {
                      if (hasChildren) {
                        toggleExpand(item.label);
                      } else {
                        navigate(item.route);
                        if (isMobile) setIsOpen(false);
                      }
                    }}
                  >
                    {item.icon}
                    <span style={{ flex: 1 }}>{item.label}</span>

                    {item.badge !== null && item.badge !== undefined && (
                      <span className="nav-badge">{item.badge}</span>
                    )}
                    {hasChildren && (
                      <motion.span
                        animate={{ rotate: isExpanded ? 0 : -90 }}
                        transition={{ duration: 0.2 }}
                        style={{ color: 'var(--sidebar-text-3)', display: 'grid' }}
                      >
                        <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"/>
                        </svg>
                      </motion.span>
                    )}
                  </motion.div>

                  <AnimatePresence>
                    {hasChildren && isExpanded && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ marginLeft: 30, marginTop: 4, overflow: 'hidden', listStyle: 'none', padding: 0 }}
                      >
                        {item.children.map((child) => {
                          const isChildActive = location.pathname === child.route;
                          return (
                            <motion.li
                              key={child.label}
                              whileHover={{ x: 3 }}
                              className="nav-item"
                              style={{
                                fontSize: 12.5,
                                paddingTop: 6,
                                paddingBottom: 6,
                                color: isChildActive ? 'var(--accent)' : undefined,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(child.route);
                                if (isMobile) setIsOpen(false);
                              }}
                            >
                              {child.label}
                            </motion.li>
                          );
                        })}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar-foot">
          <button
            onClick={() => navigate('/profile')}
            className="profile-mini"
            title="My Profile"
          >
            <UserAvatar src={profile?.image} size={32} verified ring="var(--sidebar-bg)" />
            <span style={{ minWidth: 0 }}>
              <span className="profile-name" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile?.name || 'Leo Member'}</span>
              <span className="profile-role" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{role}</span>
            </span>
          </button>

          <button
            onClick={handleLogout}
            className="nav-item"
            style={{ width: '100%', color: 'var(--error)', marginTop: 4 }}
            title="Logout"
          >
            <FaPowerOff />
            <span>Logout</span>
          </button>

          <div style={{ marginTop: 10, textAlign: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--sidebar-text-3)', letterSpacing: '0.04em' }}>
              Powered by <span style={{ fontWeight: 600, color: 'var(--sidebar-text-2)' }}>SLIIT Leos</span>
            </span>
          </div>
        </div>
      </motion.aside>
    </>
  );
};

export default React.memo(Sidebar);
